import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { IonDatetime, IonDatetimeButton, IonItem, IonLabel, IonNote, IonPopover } from '@ionic/react';
import { rangeStep } from 'lodash/fp';
import { DateTime } from 'luxon';
import { RoundedSquareIcon } from './Icon';

interface TimeRange {
    start: DateTime | null;
    end: DateTime | null;
}

const TimeRangePicker: React.FC<{
    timeRange: TimeRange;
    id: string;
    title: string;
    color?: string;
    backgroundColor?: string;
    disabled?: boolean;
    showDuration?: boolean;
    step?: number;
    onChange?: (value: TimeRange) => void;
}> = ({ timeRange, id, title, disabled, color, backgroundColor = 'white', showDuration, step = 15, onChange }) => {
    if (!timeRange.start && !timeRange.end) {
        return null;
    }

    const minuteValues = rangeStep(step, 0, 60);

    return (
        <IonItem style={{ '--background': backgroundColor }}>
            <RoundedSquareIcon icon={faClock} color={color} />
            <IonLabel className={`pl-1 ${disabled ? 'item-interactive-disabled' : ''}`}>
                {title}
                {showDuration && timeRange.start && timeRange.end && (
                    <IonNote className="pl-1 text-gray-600 text-base">
                        {timeRange.end
                            .set({ second: 0 })
                            .diff(timeRange.start.set({ second: 0 }))
                            .toFormat('h:mm')}
                    </IonNote>
                )}
            </IonLabel>
            {timeRange.start && (
                <>
                    <span className="pr-1">De</span>
                    <IonDatetimeButton datetime={`time-range-picker-${id}-start`} disabled={disabled} />
                    <IonPopover keepContentsMounted={true}>
                        <IonDatetime
                            id={`time-range-picker-${id}-start`}
                            presentation="time"
                            value={
                                step
                                    ? timeRange.start
                                          //set minute to the closest value
                                          .set({ minute: Math.round(timeRange.start.minute / step) * step })
                                          .toISO()
                                    : timeRange.start.toISO()
                            }
                            onIonChange={(e) =>
                                onChange?.({ start: DateTime.fromISO(e.detail.value as string), end: timeRange.end })
                            }
                            max={timeRange.end?.toISO() ?? undefined}
                            hourCycle="h23"
                            minuteValues={minuteValues}
                        />
                    </IonPopover>
                </>
            )}
            {timeRange.end && (
                <>
                    <span className="px-1">à</span>
                    <IonDatetimeButton datetime={`time-range-picker-${id}-end`} disabled={disabled} />
                    <IonPopover keepContentsMounted={true}>
                        <IonDatetime
                            id={`time-range-picker-${id}-end`}
                            presentation="time"
                            value={
                                step
                                    ? timeRange.end
                                          .set({ minute: Math.round(timeRange.end.minute / step) * step })
                                          .toISO()
                                    : timeRange.end.toISO()
                            }
                            onIonChange={(e) =>
                                onChange?.({ start: timeRange.start, end: DateTime.fromISO(e.detail.value as string) })
                            }
                            min={timeRange.start?.toISO() ?? undefined}
                            hourCycle="h23"
                            minuteValues={minuteValues}
                        />
                    </IonPopover>
                </>
            )}
        </IonItem>
    );
};

export default TimeRangePicker;
